import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Building2, Briefcase, ChevronDown, CheckCircle, User, Store } from "lucide-react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { detectHigherLevelRole } from "../services/userRolesService";
import { useOrgHierarchySync } from "../hooks/useOrgHierarchySync";
import { Button } from "./Button";
import { US_STATES, getAllCountryOptions, getStateOptions } from "../utils/countries";
import { regionCountryCodes } from "../utils/regions";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";
import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";
import LoadingSpinner from "./LoadingSpinner";
import Navigation from "./Navigation";

const inputCls =
  "block w-full p-2 bg-input-bg border border-border rounded-lg text-content-primary placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan text-sm";
const labelCls = "block text-sm font-medium text-content-secondary mb-1";

const roleColors = {
  OWNER: 'bg-red-100 text-red-800',
  ADMIN: 'bg-purple-100 text-purple-800',
  ENTERPRISE_RISK_MANAGER: 'bg-indigo-100 text-indigo-800',
  RISK_MANAGER: 'bg-orange-100 text-orange-800',
  CONTRIBUTOR: 'bg-blue-100 text-blue-800',
  APPROVER: 'bg-green-100 text-green-800',
  VIEWER: 'bg-gray-100 text-gray-800',
  CUSTOM: 'bg-gray-200 text-gray-700',
};

// Maps useAddressAutocomplete's org-prefixed keys → deal keys
const ORG_TO_DEAL = {
  organizationAddress1: "dealAddress",
  organizationCity: "dealCity",
  organizationState: "dealState",
  organizationZip: "dealZip",
  organizationCountry: "dealCountry",
};

const Accounts = () => {
  const { user, updateUser, userRoles } = useAuth();
  const { canAdministerOrganization, canManageOrganizationRisks, canManageDeal } = useRBAC();
  const { refreshOrgHierarchy } = useOrgHierarchySync();

  // ── Computed role display ──────────────────────────────────────────────────
  const ROLE_VALUES = { OWNER: 1, ADMIN: 2, RISK_MANAGER: 3, CONTRIBUTOR: 4, APPROVER: 5, VIEWER: 6 };
  const SCOPE_VALUES = { ACCOUNT: 1, TENANT: 2, ORG: 3, BUSINESS_UNIT: 4, DEAL: 5, SELF: 6 };
  const ROLE_NAMES_BY_VALUE = { 1: 'OWNER', 2: 'ADMIN', 3: 'RISK_MANAGER', 4: 'CONTRIBUTOR', 5: 'APPROVER', 6: 'VIEWER' };

  const { displayRole, roleColorKey } = useMemo(() => {
    if (!userRoles?.length) return { displayRole: null, roleColorKey: null };
    try {
      const selectedOrg = JSON.parse(localStorage.getItem('selectedOrganization'));
      const orgId = selectedOrg?.id || selectedOrg?.organizationId;
      if (!orgId) return { displayRole: null, roleColorKey: null };

      // Normalise roles so detectHigherLevelRole gets numeric values
      const normalised = userRoles.map((r) => ({
        ...r,
        roleValue: r.roleValue ?? (typeof r.role === 'string' ? ROLE_VALUES[r.role.toUpperCase()] : r.role),
        scopeValue: r.scopeValue ?? (typeof r.scope === 'string' ? SCOPE_VALUES[r.scope.toUpperCase()] : r.scope),
      }));

      const { matchedRole, isAmbiguous } = detectHigherLevelRole(normalised, orgId);

      if (matchedRole) {
        // Use distinct color key for Enterprise Risk Manager vs Risk Manager
        const colorKey = matchedRole.key === 'RISK_MANAGER_ORG'
          ? 'ENTERPRISE_RISK_MANAGER'
          : (ROLE_NAMES_BY_VALUE[matchedRole.role] || 'CUSTOM');
        return { displayRole: matchedRole.displayName, roleColorKey: colorKey };
      }
      if (isAmbiguous || normalised.length > 0) {
        return { displayRole: 'Custom', roleColorKey: 'CUSTOM' };
      }
      return { displayRole: null, roleColorKey: null };
    } catch {
      return { displayRole: null, roleColorKey: null };
    }
  }, [userRoles]);

  // ── Data ────────────────────────────────────────────────────────────────────
  const [organizations, setOrganizations] = useState([]);
  const [deals, setDeals] = useState([]);
  const [regions, setRegions] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Auto-dismiss success banner after 3 s
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(""), 3000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // ── Sidebar accordion ────────────────────────────────────────────────────────
  const [orgsOpen, setOrgsOpen] = useState(false);
  const [dealsOpen, setDealsOpen] = useState(false);

  // ── Selected item ────────────────────────────────────────────────────────────
  const [selectedType, setSelectedType] = useState(null); // 'org' | 'deal'
  const [selectedId, setSelectedId] = useState(null);

  // ── Edit state: org ──────────────────────────────────────────────────────────
  const [editOrg, setEditOrg] = useState(null);

  // ── Edit state: deal ─────────────────────────────────────────────────────────
  const [editDeal, setEditDeal] = useState(null);

  // ── Edit state: vendor ────────────────────────────────────────────────────────
  const [vendors, setVendors] = useState([]);
  const [vendorsOpen, setVendorsOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  // ── Edit state: profile ───────────────────────────────────────────────────────
  const [editProfile, setEditProfile] = useState(null);

  // Keep editProfile in sync with user object (e.g. after save updates AuthContext)
  useEffect(() => {
    if (selectedType === "general" && user) {
      setEditProfile((prev) => ({
        ...prev,
        fullName: user.userName || user.fullName || prev?.fullName || "",
        email: user.email || prev?.email || "",
        role: user.role || prev?.role || "",
      }));
    }
  }, [user, selectedType]);

  // ── Google Maps ───────────────────────────────────────────────────────────────
  const { isLoaded: googleIsLoaded } = useGoogleMapsLoader();

  // ── Org address autocomplete ─────────────────────────────────────────────────
  const handleBatchChangeOrg = useCallback((fields) => {
    setEditOrg((prev) => {
      if (!prev) return prev;
      const updates = {};
      Object.entries(fields).forEach(([k, v]) => { if (v !== undefined) updates[k] = v; });
      return { ...prev, ...updates };
    });
  }, []);

  const orgRegion = regions.find((r) => String(r.regionId) === String(editOrg?.regionId));
  // Keyed on countryFilterKey (not the display name, which an operator may override).
  const orgRegionCodes = useMemo(
    () => regionCountryCodes(orgRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgRegion?.countryFilterKey]
  );
  const orgCountryOptions = getAllCountryOptions();

  const {
    suggestions: orgSuggestions,
    showSuggestions: orgShowSuggestions,
    setShowSuggestions: setOrgShowSuggestions,
    handleSuggestionSelect: orgHandleSuggestionSelect,
    zipLoading: orgZipLoading,
  } = useAddressAutocomplete({
    isLoaded: googleIsLoaded,
    addressValue: editOrg?.organizationAddress1 ?? "",
    country: editOrg?.organizationCountry ?? "",
    regionCodes: orgRegionCodes,
    zip: editOrg?.organizationZip ?? "",
    city: editOrg?.organizationCity ?? "",
    state: editOrg?.organizationState ?? "",
    onBatchChange: handleBatchChangeOrg,
  });

  const orgAddressRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (orgAddressRef.current && !orgAddressRef.current.contains(e.target))
        setOrgShowSuggestions(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [setOrgShowSuggestions]);

  const orgSubVerticals =
    verticals.find((v) => v.verticalId === Number(editOrg?.verticalId))?.subVerticals || [];

  // ── Deal address autocomplete ────────────────────────────────────────────────
  const handleBatchChangeDeal = useCallback((fields) => {
    setEditDeal((prev) => {
      if (!prev) return prev;
      const mapped = {};
      for (const [key, val] of Object.entries(fields)) {
        mapped[ORG_TO_DEAL[key] ?? key] = val;
      }
      return { ...prev, ...mapped };
    });
  }, []);

  const dealRegion = regions.find((r) => String(r.regionId) === String(editDeal?.regionId));
  // Keyed on countryFilterKey (not the display name, which an operator may override).
  const dealRegionCodes = useMemo(
    () => regionCountryCodes(dealRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealRegion?.countryFilterKey]
  );
  const dealCountryOptions = getAllCountryOptions();

  const {
    suggestions: dealSuggestions,
    showSuggestions: dealShowSuggestions,
    setShowSuggestions: setDealShowSuggestions,
    handleSuggestionSelect: dealHandleSuggestionSelect,
    zipLoading: dealZipLoading,
  } = useAddressAutocomplete({
    isLoaded: googleIsLoaded,
    addressValue: editDeal?.dealAddress ?? "",
    country: editDeal?.dealCountry ?? "",
    regionCodes: dealRegionCodes,
    zip: editDeal?.dealZip ?? "",
    city: editDeal?.dealCity ?? "",
    state: editDeal?.dealState ?? "",
    onBatchChange: handleBatchChangeDeal,
  });

  const dealAddressRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (dealAddressRef.current && !dealAddressRef.current.contains(e.target))
        setDealShowSuggestions(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [setDealShowSuggestions]);

  const dealSubVerticals =
    verticals.find((v) => v.verticalId === parseInt(editDeal?.verticalId))?.subVerticals || [];

  // ── RBAC ──────────────────────────────────────────────────────────────────────
  // Filtered lists — only entities the current user can manage
  const visibleOrgs = organizations.filter((org) => canAdministerOrganization(org.organizationId));
  const visibleDeals = deals.filter((deal) => {
    const dId = deal.dealID ?? deal.dealId ?? deal.DealId;
    const orgId = deal.organizationId || deal.OrganizationId;
    return canAdministerOrganization(orgId) || canManageDeal(dId);
  });
  const canSeeVendors = canManageOrganizationRisks();

  // ── Data fetching ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [orgsRes, dealsRes, regionsRes, verticalsRes, vendorsRes] = await Promise.allSettled([
          api.get("/Organizations"),
          api.get("/Deals"),
          api.get("/Regions"),
          api.get("/Verticals"),
          api.get("/TPRMVendor/vendors?pageSize=100"),
        ]);
        if (orgsRes.status === "fulfilled") setOrganizations(orgsRes.value.data || []);
        if (dealsRes.status === "fulfilled") setDeals(dealsRes.value.data || []);
        if (regionsRes.status === "fulfilled") setRegions(regionsRes.value.data || []);
        if (verticalsRes.status === "fulfilled") setVerticals(verticalsRes.value.data || []);
        if (vendorsRes.status === "fulfilled") setVendors(vendorsRes.value.data?.items || vendorsRes.value.data || []);
      } catch (err) {} finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // ── Select org ───────────────────────────────────────────────────────────────
  const selectOrg = (org) => {
    setEditOrg({
      organizationId: org.organizationId,
      organizationName: org.organizationName || "",
      companySize: org.companySize || "",
      annualRevenue: org.annualRevenue || "",
      organizationAddress1: org.organizationAddress1 || "",
      organizationAddress2: org.organizationAddress2 || "",
      organizationCity: org.organizationCity || "",
      organizationState: org.organizationState || "",
      organizationZip: org.organizationPostalCode || "",
      organizationContactNo1: org.organizationContactNo1 || "",
      organizationContactNo2: org.organizationContactNo2 || "",
      organizationCountry: org.organizationCountry || "",
      regionId: org.regionId || "",
      verticalId: org.verticalId || "",
      subVerticalId: org.subVerticalId || "",
      allowedIpRanges: org.allowedIpRanges
        ? org.allowedIpRanges.split(",").map(s => s.trim()).filter(Boolean)
        : [],
    });
    setSelectedType("org");
    setSelectedId(org.organizationId);
    setSuccessMsg("");
  };

  // ── Org input handler ────────────────────────────────────────────────────────
  const handleOrgInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "regionId") {
      setEditOrg((prev) => ({
        ...prev,
        regionId: value === "" ? "" : Number(value),
      }));
    } else if (name === "verticalId") {
      setEditOrg((prev) => ({
        ...prev,
        verticalId: value === "" ? "" : Number(value),
        subVerticalId: "",
      }));
    } else if (name === "organizationCountry") {
      setEditOrg((prev) => ({ ...prev, organizationCountry: value, organizationState: "" }));
    } else if (name === "subVerticalId") {
      setEditOrg((prev) => ({ ...prev, subVerticalId: value === "" ? "" : Number(value) }));
    } else {
      setEditOrg((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ── IP Range handlers ─────────────────────────────────────────────────────
  const CIDR_REGEX = /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/;

  const handleAddIpRange = () =>
    setEditOrg(prev => ({ ...prev, allowedIpRanges: [...(prev.allowedIpRanges || []), ""] }));

  const handleIpRangeChange = (index, value) =>
    setEditOrg(prev => {
      const updated = [...(prev.allowedIpRanges || [])];
      updated[index] = value;
      return { ...prev, allowedIpRanges: updated };
    });

  const handleRemoveIpRange = (index) =>
    setEditOrg(prev => ({
      ...prev,
      allowedIpRanges: (prev.allowedIpRanges || []).filter((_, i) => i !== index),
    }));

  // ── Save org ─────────────────────────────────────────────────────────────────
  const handleUpdateOrg = async () => {
    if (!editOrg.regionId || !editOrg.organizationCountry || !editOrg.verticalId || !editOrg.subVerticalId) {
      alert("Please fill in all required fields.");
      return;
    }
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/;
    const invalidIps = (editOrg.allowedIpRanges || []).filter(ip => ip.trim() && !cidrRegex.test(ip.trim()));
    if (invalidIps.length > 0) {
      alert(`Invalid IP range format: ${invalidIps.join(", ")}\nUse CIDR notation (e.g. 192.168.1.0/24) or a plain IP.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        organizationName: editOrg.organizationName,
        companySize: editOrg.companySize || null,
        annualRevenue: editOrg.annualRevenue || null,
        organizationAddress1: editOrg.organizationAddress1,
        organizationAddress2: editOrg.organizationAddress2,
        organizationCity: editOrg.organizationCity || null,
        organizationState: editOrg.organizationState || null,
        organizationPostalCode: editOrg.organizationZip || null,
        organizationContactNo1: editOrg.organizationContactNo1,
        organizationContactNo2: editOrg.organizationContactNo2,
        organizationCountry: editOrg.organizationCountry,
        regionId: Number(editOrg.regionId),
        verticalId: Number(editOrg.verticalId),
        subVerticalId: Number(editOrg.subVerticalId),
        allowedIpRanges: (editOrg.allowedIpRanges || []).filter(ip => ip.trim()).join(",") || null,
      };
      const response = await api.put(`/Organizations/${editOrg.organizationId}`, payload);
      setOrganizations((prev) =>
        prev.map((o) =>
          o.organizationId === editOrg.organizationId ? { ...o, ...response.data } : o
        )
      );
      refreshOrgHierarchy();
      setSuccessMsg("Organization updated successfully.");
    } catch (err) {
      alert("Failed to update organization. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Select deal ──────────────────────────────────────────────────────────────
  const selectDeal = (deal) => {
    const id = deal.dealID ?? deal.dealId ?? deal.DealId;
    setEditDeal({
      dealId: id,
      dealName: deal.dealName || deal.DealName || "",
      regionId: String(deal.regionId || deal.RegionId || ""),
      verticalId: String(deal.verticalId || deal.VerticalId || ""),
      subVerticalId: String(deal.subVerticalId || deal.SubVerticalId || ""),
      domain: deal.domain || deal.Domain || "",
      dealCategory: deal.dealCategory || deal.DealCategory || "",
      organizationId: deal.organizationId || deal.OrganizationId || "",
      dealOwner: deal.dealOwner || deal.DealOwner || "",
      dealStartDate: (deal.dealStartDate || deal.DealStartDate || "").split("T")[0],
      dealEndDate: (deal.dealEndDate || deal.DealEndDate || "").split("T")[0],
      dealCountry: deal.dealCountry || deal.DealCountry || "",
      dealAddress: deal.dealAddress || deal.DealAddress || "",
      dealCity: deal.dealCity || deal.DealCity || "",
      dealState: deal.dealState || deal.DealState || "",
      dealZip: deal.dealZip || deal.DealZip || "",
      dealPhoneNo: deal.dealPhoneNo || deal.DealPhoneNo || "",
      dealStatus: deal.dealStatus || deal.DealStatus || "",
    });
    setSelectedType("deal");
    setSelectedId(id);
    setSuccessMsg("");
  };

  // ── Deal input handler ───────────────────────────────────────────────────────
  const handleDealInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "regionId") {
      setEditDeal((prev) => ({ ...prev, regionId: value }));
    } else if (name === "verticalId") {
      setEditDeal((prev) => ({ ...prev, verticalId: value, subVerticalId: "" }));
    } else if (name === "dealCountry") {
      setEditDeal((prev) => ({ ...prev, dealCountry: value, dealState: "" }));
    } else {
      setEditDeal((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ── Save deal ────────────────────────────────────────────────────────────────
  const handleUpdateDeal = async () => {
    if (!editDeal.dealName || !editDeal.regionId || !editDeal.verticalId || !editDeal.dealStatus) {
      alert("Please fill in all required fields (Deal Name, Region, Vertical, Status).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        DealName: editDeal.dealName,
        RegionId: editDeal.regionId ? parseInt(editDeal.regionId, 10) : null,
        VerticalId: editDeal.verticalId ? parseInt(editDeal.verticalId, 10) : null,
        SubVerticalId: editDeal.subVerticalId ? parseInt(editDeal.subVerticalId, 10) : null,
        Domain: editDeal.domain,
        DealCategory: editDeal.dealCategory,
        OrganizationId: editDeal.organizationId,
        DealOwner: editDeal.dealOwner,
        DealStartDate: editDeal.dealStartDate || null,
        DealEndDate: editDeal.dealEndDate || null,
        DealCountry: editDeal.dealCountry,
        DealAddress: editDeal.dealAddress,
        DealCity: editDeal.dealCity,
        DealState: editDeal.dealState,
        DealZip: editDeal.dealZip,
        DealPhoneNo: editDeal.dealPhoneNo,
        DealStatus: editDeal.dealStatus,
      };
      const response = await api.put(`/Deals/${editDeal.dealId}`, payload);
      const updated = response.data;
      setDeals((prev) =>
        prev.map((d) => {
          const dId = d.dealID ?? d.dealId ?? d.DealId;
          return dId === editDeal.dealId ? { ...d, ...updated } : d;
        })
      );
      // Keep the edit form in sync with saved values
      setEditDeal((prev) => ({
        ...prev,
        dealName: updated.dealName ?? prev.dealName,
        regionId: updated.regionId != null ? String(updated.regionId) : prev.regionId,
        verticalId: updated.verticalId != null ? String(updated.verticalId) : prev.verticalId,
        subVerticalId: updated.subVerticalId != null ? String(updated.subVerticalId) : prev.subVerticalId,
        domain: updated.domain ?? prev.domain,
        dealCategory: updated.dealCategory ?? prev.dealCategory,
        dealOwner: updated.dealOwner ?? prev.dealOwner,
        dealStartDate: updated.dealStartDate ? updated.dealStartDate.split("T")[0] : prev.dealStartDate,
        dealEndDate: updated.dealEndDate ? updated.dealEndDate.split("T")[0] : prev.dealEndDate,
        dealCountry: updated.dealCountry ?? prev.dealCountry,
        dealAddress: updated.dealAddress ?? prev.dealAddress,
        dealCity: updated.dealCity ?? prev.dealCity,
        dealState: updated.dealState ?? prev.dealState,
        dealZip: updated.dealZip ?? prev.dealZip,
        dealPhoneNo: updated.dealPhoneNo ?? prev.dealPhoneNo,
        dealStatus: updated.dealStatus ?? prev.dealStatus,
      }));
      setSuccessMsg("Deal updated successfully.");
    } catch (err) {
      alert("Failed to update deal. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Select general (user profile) ────────────────────────────────────────────
  const selectGeneral = () => {
    setEditProfile({
      fullName: user?.userName || user?.fullName || "",
      email: user?.email || "",
      role: user?.role || "",
    });
    setSelectedType("general");
    setSelectedId("general");
    setSuccessMsg("");
  };

  // ── Save profile ──────────────────────────────────────────────────────────────
  const handleUpdateProfile = async () => {
    if (!editProfile?.fullName?.trim()) {
      alert("Full name is required.");
      return;
    }
    setSaving(true);
    try {
      const saved = editProfile.fullName.trim();
      await api.put("/Users/me", { fullName: saved });
      // Update user in AuthContext + localStorage so the nav/UI reflects the new name
      updateUser({ userName: saved });
      setSuccessMsg("Profile updated successfully.");
    } catch (err) {
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Select vendor ─────────────────────────────────────────────────────────────
  const selectVendor = (vendor) => {
    setEditVendor({
      vendorId: vendor.vendorId || vendor.VendorId,
      vendorName: vendor.vendorName || vendor.VendorName || "",
      vendorAddress1: vendor.vendorAddress1 || vendor.VendorAddress1 || "",
      vendorAddress2: vendor.vendorAddress2 || vendor.VendorAddress2 || "",
      vendorContactNo1: vendor.vendorContactNo1 || vendor.VendorContactNo1 || "",
      vendorContactNo2: vendor.vendorContactNo2 || vendor.VendorContactNo2 || "",
      vendorOwner: vendor.vendorOwner || vendor.VendorOwner || "",
      vendorDomain: vendor.vendorDomain || vendor.VendorDomain || "",
      vendorDescription: vendor.vendorDescription || vendor.VendorDescription || "",
    });
    setSelectedType("vendor");
    setSelectedId(vendor.vendorId || vendor.VendorId);
    setSuccessMsg("");
  };

  // ── Vendor input handler ──────────────────────────────────────────────────────
  const handleVendorInputChange = (e) => {
    const { name, value } = e.target;
    setEditVendor((prev) => ({ ...prev, [name]: value }));
  };

  // ── Save vendor ───────────────────────────────────────────────────────────────
  const handleUpdateVendor = async () => {
    if (!editVendor?.vendorName?.trim()) {
      alert("Vendor name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        VendorName: editVendor.vendorName,
        VendorAddress1: editVendor.vendorAddress1,
        VendorAddress2: editVendor.vendorAddress2,
        VendorContactNo1: editVendor.vendorContactNo1,
        VendorContactNo2: editVendor.vendorContactNo2,
        VendorOwner: editVendor.vendorOwner,
        VendorDomain: editVendor.vendorDomain,
        VendorDescription: editVendor.vendorDescription,
        OrganizationId: 0,
      };
      const response = await api.put(`/TPRMVendor/${editVendor.vendorId}`, payload);
      const updated = response.data;
      setVendors((prev) =>
        prev.map((v) =>
          (v.vendorId || v.VendorId) === editVendor.vendorId ? { ...v, ...updated } : v
        )
      );
      setSuccessMsg("Vendor updated successfully.");
    } catch (err) {
      alert("Failed to update vendor. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <><Navigation /><LoadingSpinner /></>;

  return (
    <div className="min-h-screen bg-theme-primary flex flex-col">
      <Navigation />

      <div className="flex flex-1 overflow-hidden" style={{ height: "calc(100vh - 96px)" }}>

        {/* ── Left Sidebar ──────────────────────────────────────────────────── */}
        <div className="w-72 flex-shrink-0 border-r border-border bg-theme-secondary flex flex-col overflow-y-auto">

          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-bold text-content-primary">Accounts</h2>
          </div>

          {/* General */}
          <button
            onClick={selectGeneral}
            className={`flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors border-l-2 ${
              selectedType === "general"
                ? "bg-accent-cyan/10 text-accent-cyan border-accent-cyan"
                : "text-content-primary hover:bg-theme-hover border-transparent"
            }`}
          >
            <User className="h-4 w-4" />
            General
          </button>

          {/* Organizations accordion */}
          <div>
            <button
              onClick={() => setOrgsOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-content-primary hover:bg-theme-hover transition-colors"
            >
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-accent-cyan" />
                Organizations
              </span>
              <ChevronDown
                className={`h-4 w-4 text-content-muted transition-transform ${orgsOpen ? "rotate-180" : ""}`}
              />
            </button>

            {orgsOpen && (
              <div className="pb-1">
                {visibleOrgs.length === 0 ? (
                  <p className="px-6 py-2 text-xs text-content-muted">No organizations found.</p>
                ) : (
                  visibleOrgs.map((org) => (
                    <button
                      key={org.organizationId}
                      onClick={() => selectOrg(org)}
                      className={`w-full text-left px-6 py-2 text-sm transition-colors ${
                        selectedType === "org" && selectedId === org.organizationId
                          ? "bg-accent-cyan/10 text-accent-cyan border-l-2 border-accent-cyan font-medium"
                          : "text-content-secondary hover:bg-theme-hover hover:text-content-primary border-l-2 border-transparent"
                      }`}
                    >
                      {org.organizationName}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Deals accordion */}
          <div>
            <button
              onClick={() => setDealsOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-content-primary hover:bg-theme-hover transition-colors border-t border-border"
            >
              <span className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-accent-purple" />
                Deals
              </span>
              <ChevronDown
                className={`h-4 w-4 text-content-muted transition-transform ${dealsOpen ? "rotate-180" : ""}`}
              />
            </button>

            {dealsOpen && (
              <div className="pb-1">
                {visibleDeals.length === 0 ? (
                  <p className="px-6 py-2 text-xs text-content-muted">No deals found.</p>
                ) : (
                  visibleDeals.map((deal) => {
                    const dId = deal.dealID ?? deal.dealId ?? deal.DealId;
                    const dName = deal.dealName || deal.DealName || `Deal ${dId}`;
                    return (
                      <button
                        key={dId}
                        onClick={() => selectDeal(deal)}
                        className={`w-full text-left px-6 py-2 text-sm transition-colors ${
                          selectedType === "deal" && selectedId === dId
                            ? "bg-accent-purple/10 text-accent-purple border-l-2 border-accent-purple font-medium"
                            : "text-content-secondary hover:bg-theme-hover hover:text-content-primary border-l-2 border-transparent"
                        }`}
                      >
                        {dName}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Vendors accordion */}
          {canSeeVendors && <div>
            <button
              onClick={() => setVendorsOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-content-primary hover:bg-theme-hover transition-colors border-t border-border"
            >
              <span className="flex items-center gap-2">
                <Store className="h-4 w-4 text-accent-cyan" />
                Vendors
              </span>
              <ChevronDown
                className={`h-4 w-4 text-content-muted transition-transform ${vendorsOpen ? "rotate-180" : ""}`}
              />
            </button>

            {vendorsOpen && (
              <div className="pb-1">
                {vendors.length === 0 ? (
                  <p className="px-6 py-2 text-xs text-content-muted">No vendors found.</p>
                ) : (
                  vendors.map((vendor) => {
                    const vId = vendor.vendorId || vendor.VendorId;
                    const vName = vendor.vendorName || vendor.VendorName || `Vendor ${vId}`;
                    return (
                      <button
                        key={vId}
                        onClick={() => selectVendor(vendor)}
                        className={`w-full text-left px-6 py-2 text-sm transition-colors ${
                          selectedType === "vendor" && selectedId === vId
                            ? "bg-accent-cyan/10 text-accent-cyan border-l-2 border-accent-cyan font-medium"
                            : "text-content-secondary hover:bg-theme-hover hover:text-content-primary border-l-2 border-transparent"
                        }`}
                      >
                        {vName}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>}
        </div>

        {/* ── Right Panel ───────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Success banner */}
          {successMsg && (
            <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg animate-fade-in">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">{successMsg}</span>
            </div>
          )}

          {/* Empty state */}
          {!selectedType && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-content-inverse" />
              </div>
              <h3 className="text-xl font-semibold text-content-primary mb-2">Select an Account</h3>
              <p className="text-content-secondary text-sm max-w-xs">
                Choose an organization or deal from the left panel to view and edit its details.
              </p>
            </div>
          )}

          {/* ── Org Edit Form ─────────────────────────────────────────────── */}
          {selectedType === "org" && editOrg && (
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-content-primary">Edit Organization</h2>
              </div>

              <div className="space-y-4">

                {/* Organization Name */}
                <div>
                  <label className={labelCls}>Organization Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="organizationName"
                    value={editOrg.organizationName}
                    onChange={handleOrgInputChange}
                    placeholder="Enter organization name"
                    className={inputCls}
                  />
                </div>

                {/* Company Size | Annual Revenue */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Company Size</label>
                    <select
                      name="companySize"
                      value={editOrg.companySize}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                      >
                      <option value="" className="bg-theme-secondary">-- Select Company Size --</option>
                      <option value="small" className="bg-theme-secondary">Small (1–50 employees)</option>
                      <option value="medium" className="bg-theme-secondary">Medium (51–500 employees)</option>
                      <option value="large" className="bg-theme-secondary">Large (501–5,000 employees)</option>
                      <option value="enterprise" className="bg-theme-secondary">Enterprise (5,000+ employees)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Annual Revenue</label>
                    <select
                      name="annualRevenue"
                      value={editOrg.annualRevenue}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                      >
                      <option value="" className="bg-theme-secondary">-- Select Annual Revenue --</option>
                      <option value="<1M" className="bg-theme-secondary">Less than $1M</option>
                      <option value="1-10M" className="bg-theme-secondary">$1M – $10M</option>
                      <option value="10-50M" className="bg-theme-secondary">$10M – $50M</option>
                      <option value="50-100M" className="bg-theme-secondary">$50M – $100M</option>
                      <option value=">100M" className="bg-theme-secondary">More than $100M</option>
                    </select>
                  </div>
                </div>

                {/* Region | Country */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Data Residency Region <span className="text-red-500">*</span></label>
                    <select
                      name="regionId"
                      value={editOrg.regionId}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                      >
                      <option value="" className="bg-theme-secondary">-- Select Region --</option>
                      {regions.map((r) => (
                        <option key={r.regionId} value={r.regionId} className="bg-theme-secondary">
                          {r.regionName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Country <span className="text-red-500">*</span></label>
                    <select
                      name="organizationCountry"
                      value={editOrg.organizationCountry}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                      >
                      {orgCountryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} className="bg-theme-secondary">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Street Address */}
                <div ref={orgAddressRef} className="relative">
                  <label className={labelCls}>Street Address</label>
                  <input
                    type="text"
                    name="organizationAddress1"
                    value={editOrg.organizationAddress1}
                    onChange={handleOrgInputChange}
                    autoComplete="off"
                    onFocus={() => orgSuggestions.length > 0 && setOrgShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setOrgShowSuggestions(false), 150)}
                    placeholder="Start typing to search address…"
                    className={inputCls}
                  />
                  {orgShowSuggestions && orgSuggestions.length > 0 && (
                    <ul className="absolute z-50 w-full bg-theme-secondary border border-border rounded-xl shadow-theme-xl overflow-hidden" style={{ top: "calc(100% + 2px)" }}>
                      {orgSuggestions.map((s, i) => (
                        <li key={s.placePrediction?.placeId || i} className="border-b border-border last:border-b-0">
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => orgHandleSuggestionSelect(s)}
                            className="w-full px-4 py-2.5 text-left hover:bg-theme-primary transition-colors"
                          >
                            <p className="text-sm font-medium text-content-primary truncate">
                              {s.placePrediction?.mainText?.text}
                            </p>
                            {s.placePrediction?.secondaryText?.text && (
                              <p className="text-xs text-content-muted truncate mt-0.5">
                                {s.placePrediction.secondaryText.text}
                              </p>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Address 2 */}
                <div>
                  <label className={labelCls}>Address 2</label>
                  <input
                    type="text"
                    name="organizationAddress2"
                    value={editOrg.organizationAddress2}
                    onChange={handleOrgInputChange}
                    placeholder="Apt, suite, unit, etc."
                    className={inputCls}
                  />
                </div>

                {/* ZIP | State | City */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelCls}>ZIP / Postal</label>
                    <input
                      type="text"
                      name="organizationZip"
                      value={editOrg.organizationZip}
                      onChange={handleOrgInputChange}
                      placeholder="ZIP"
                      className={inputCls}
                      />
                    {orgZipLoading && (
                      <p className="text-xs text-content-muted mt-0.5 animate-pulse">Looking up…</p>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>State / Province</label>
                    {editOrg.organizationCountry === "US" ? (
                      <select
                        name="organizationState"
                        value={editOrg.organizationState}
                        onChange={handleOrgInputChange}
                        className={inputCls}
                          >
                        <option value="" className="bg-theme-secondary">-- State --</option>
                        {US_STATES.map((s) => (
                          <option key={s.code} value={s.code} className="bg-theme-secondary">
                            {s.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="organizationState"
                        value={editOrg.organizationState}
                        onChange={handleOrgInputChange}
                        placeholder="State / Province"
                        className={inputCls}
                          />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input
                      type="text"
                      name="organizationCity"
                      value={editOrg.organizationCity}
                      onChange={handleOrgInputChange}
                      placeholder="City"
                      className={inputCls}
                      />
                  </div>
                </div>

                {/* Vertical | Sub-Vertical */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Vertical <span className="text-red-500">*</span></label>
                    <select
                      name="verticalId"
                      value={editOrg.verticalId}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                      >
                      <option value="" className="bg-theme-secondary">-- Select Vertical --</option>
                      {verticals.map((v) => (
                        <option key={v.verticalId} value={v.verticalId} className="bg-theme-secondary">
                          {v.verticalName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Sub-Vertical <span className="text-red-500">*</span></label>
                    <select
                      name="subVerticalId"
                      value={editOrg.subVerticalId}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                    >
                      <option value="" className="bg-theme-secondary">-- Select Sub-Vertical --</option>
                      {orgSubVerticals.map((sv) => (
                        <option key={sv.subVerticalId} value={sv.subVerticalId} className="bg-theme-secondary">
                          {sv.subVerticalName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Phone No 1 | Phone No 2 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Phone No</label>
                    <input
                      type="text"
                      name="organizationContactNo1"
                      value={editOrg.organizationContactNo1}
                      onChange={handleOrgInputChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputCls}
                      />
                  </div>
                  <div>
                    <label className={labelCls}>Phone No 2</label>
                    <input
                      type="text"
                      name="organizationContactNo2"
                      value={editOrg.organizationContactNo2}
                      onChange={handleOrgInputChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputCls}
                      />
                  </div>
                </div>

              </div>

              {/* ── IP Restrictions ──────────────────────────────────────── */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <label className={labelCls}>Allowed IP Ranges <span className="text-xs text-content-muted font-normal">(optional)</span></label>
                    <p className="text-xs text-content-muted mt-0.5">
                      If set, only requests from these IPs / CIDR ranges can access this organization.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddIpRange}
                    className="text-xs font-semibold text-accent-cyan hover:underline flex-shrink-0 ml-3"
                  >
                    + Add Range
                  </button>
                </div>

                {(editOrg.allowedIpRanges || []).length === 0 ? (
                  <p className="text-xs text-content-muted italic py-2">No restrictions — all IPs allowed.</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {(editOrg.allowedIpRanges || []).map((ip, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={ip}
                          onChange={(e) => handleIpRangeChange(index, e.target.value)}
                          placeholder="e.g. 192.168.1.0/24 or 10.0.0.5"
                          autoComplete="off"
                          className={`${inputCls} flex-1 font-mono text-xs ${
                            ip && !CIDR_REGEX.test(ip.trim())
                              ? "border-red-500 focus:ring-red-400/50 focus:border-red-500"
                              : ""
                          }`}
                        />
                        {ip && !CIDR_REGEX.test(ip.trim()) && (
                          <span className="text-xs text-red-500 flex-shrink-0">Invalid</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveIpRange(index)}
                          className="flex-shrink-0 w-7 h-7 rounded-lg border border-border bg-theme-secondary hover:bg-red-50 hover:border-red-300 flex items-center justify-center transition-colors"
                          title="Remove"
                        >
                          <svg className="w-3.5 h-3.5 text-content-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={handleUpdateOrg} disabled={saving} variant="primary" size="md">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}

          {/* ── Deal Edit Form ────────────────────────────────────────────── */}
          {selectedType === "deal" && editDeal && (
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-content-primary">Edit Deal</h2>
              </div>

              <div className="space-y-4">

                {/* Deal Name */}
                <div>
                  <label className={labelCls}>Deal Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="dealName"
                    value={editDeal.dealName}
                    onChange={handleDealInputChange}
                    placeholder="Enter deal name"
                    className={inputCls}
                  />
                </div>

                {/* Region | Country */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Data Residency Region <span className="text-red-500">*</span></label>
                    <select
                      name="regionId"
                      value={editDeal.regionId}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                    >
                      <option value="" className="bg-theme-secondary">-- Select Region --</option>
                      {regions.map((r) => (
                        <option key={r.regionId} value={r.regionId} className="bg-theme-secondary">
                          {r.regionName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Country</label>
                    <select
                      name="dealCountry"
                      value={editDeal.dealCountry}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                    >
                      {dealCountryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} className="bg-theme-secondary">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Vertical | Sub-Vertical */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Vertical <span className="text-red-500">*</span></label>
                    <select
                      name="verticalId"
                      value={editDeal.verticalId}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                    >
                      <option value="" className="bg-theme-secondary">-- Select Vertical --</option>
                      {verticals.map((v) => (
                        <option key={v.verticalId} value={v.verticalId} className="bg-theme-secondary">
                          {v.verticalName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Sub-Vertical</label>
                    <select
                      name="subVerticalId"
                      value={editDeal.subVerticalId}
                      disabled
                      className={`${inputCls} opacity-50 cursor-not-allowed`}
                    >
                      <option value="" className="bg-theme-secondary">-- Select Sub-Vertical --</option>
                      {dealSubVerticals.map((sv) => (
                        <option key={sv.subVerticalId} value={sv.subVerticalId} className="bg-theme-secondary">
                          {sv.subVerticalName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Domain | Deal Owner */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Domain</label>
                    <input
                      type="text"
                      name="domain"
                      value={editDeal.domain}
                      onChange={handleDealInputChange}
                      placeholder="e.g., example.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Deal Owner</label>
                    <input
                      type="text"
                      name="dealOwner"
                      value={editDeal.dealOwner}
                      onChange={handleDealInputChange}
                      placeholder="Owner name"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Start Date | End Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Start Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      name="dealStartDate"
                      value={editDeal.dealStartDate}
                      onChange={handleDealInputChange}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>End Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      name="dealEndDate"
                      value={editDeal.dealEndDate}
                      min={editDeal.dealStartDate || undefined}
                      onChange={handleDealInputChange}
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Address */}
                <div ref={dealAddressRef} className="relative">
                  <label className={labelCls}>Address</label>
                  <input
                    type="text"
                    name="dealAddress"
                    value={editDeal.dealAddress}
                    onChange={handleDealInputChange}
                    autoComplete="off"
                    onFocus={() => dealSuggestions.length > 0 && setDealShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setDealShowSuggestions(false), 150)}
                    placeholder="Start typing to search address…"
                    className={inputCls}
                  />
                  {dealShowSuggestions && dealSuggestions.length > 0 && (
                    <ul className="absolute z-50 w-full bg-theme-secondary border border-border rounded-xl shadow-theme-xl overflow-hidden" style={{ top: "calc(100% + 2px)" }}>
                      {dealSuggestions.map((s, i) => (
                        <li key={s.placePrediction?.placeId || i} className="border-b border-border last:border-b-0">
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => dealHandleSuggestionSelect(s)}
                            className="w-full px-4 py-2.5 text-left hover:bg-theme-primary transition-colors"
                          >
                            <p className="text-sm font-medium text-content-primary truncate">
                              {s.placePrediction?.mainText?.text}
                            </p>
                            {s.placePrediction?.secondaryText?.text && (
                              <p className="text-xs text-content-muted truncate mt-0.5">
                                {s.placePrediction.secondaryText.text}
                              </p>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* ZIP | State | City */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelCls}>Postal Code</label>
                    <input
                      type="text"
                      name="dealZip"
                      value={editDeal.dealZip}
                      onChange={handleDealInputChange}
                      placeholder="ZIP"
                      className={inputCls}
                    />
                    {dealZipLoading && (
                      <p className="text-xs text-content-muted mt-0.5 animate-pulse">Looking up…</p>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>State / Province</label>
                    {editDeal.dealCountry === "US" ? (
                      <select
                        name="dealState"
                        value={editDeal.dealState}
                        onChange={handleDealInputChange}
                        className={inputCls}
                      >
                        {getStateOptions().map((opt) => (
                          <option key={opt.value} value={opt.value} className="bg-theme-secondary">
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="dealState"
                        value={editDeal.dealState}
                        onChange={handleDealInputChange}
                        placeholder="State / Province"
                        className={inputCls}
                      />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input
                      type="text"
                      name="dealCity"
                      value={editDeal.dealCity}
                      onChange={handleDealInputChange}
                      placeholder="City"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Phone No | Status */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Phone No</label>
                    <input
                      type="text"
                      name="dealPhoneNo"
                      value={editDeal.dealPhoneNo}
                      onChange={handleDealInputChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Status <span className="text-red-500">*</span></label>
                    <select
                      name="dealStatus"
                      value={editDeal.dealStatus}
                      onChange={handleDealInputChange}
                      className={inputCls}
                    >
                      <option value="" className="bg-theme-secondary">-- Select Status --</option>
                      <option value="Active" className="bg-theme-secondary">Active</option>
                      <option value="Pending" className="bg-theme-secondary">Pending</option>
                      <option value="In Progress" className="bg-theme-secondary">In Progress</option>
                      <option value="Completed" className="bg-theme-secondary">Completed</option>
                      <option value="On Hold" className="bg-theme-secondary">On Hold</option>
                      <option value="Cancelled" className="bg-theme-secondary">Cancelled</option>
                    </select>
                  </div>
                </div>

              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={handleUpdateDeal} disabled={saving} variant="primary" size="md">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}

          {/* ── General / User Profile ────────────────────────────────────── */}
          {selectedType === "general" && editProfile && (
            <div className="max-w-2xl mx-auto">
              {/* Profile header card */}
              <div className="bg-theme-secondary border border-border rounded-2xl p-6 mb-6 flex items-center gap-5">
                <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center flex-shrink-0 text-2xl font-bold text-white">
                  {editProfile.fullName
                    ? editProfile.fullName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
                    : "?"}
                </div>
                <div>
                  <p className="text-lg font-bold text-content-primary">{editProfile.fullName || "—"}</p>
                  {editProfile.email && (
                    <p className="text-sm text-content-muted mt-0.5">{editProfile.email}</p>
                  )}
                  {displayRole && (
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[roleColorKey] || roleColors.CUSTOM}`}>
                      {displayRole}
                    </span>
                  )}
                </div>
              </div>

              {/* Profile fields */}
              <div className="bg-theme-secondary border border-border rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-border">
                  <h3 className="text-base font-semibold text-content-primary">Profile Information</h3>
                </div>

                <div className="p-6 space-y-5">
                  {/* Full Name */}
                  <div>
                    <label className={labelCls}>Full Name</label>
                    <input
                      type="text"
                      value={editProfile.fullName}
                      onChange={(e) => setEditProfile((prev) => ({ ...prev, fullName: e.target.value }))}
                      placeholder="Your full name"
                      className={inputCls}
                    />
                  </div>

                  {/* Email (read-only) */}
                  {editProfile.email && (
                    <div>
                      <label className={labelCls}>Email Address</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={editProfile.email}
                          readOnly
                          className={`${inputCls} opacity-60 cursor-not-allowed flex-1`}
                        />
                        <span className="text-xs text-content-muted whitespace-nowrap">Managed by Azure AD</span>
                      </div>
                    </div>
                  )}

                  {/* Role (read-only) */}
                  {displayRole && (
                    <div>
                      <label className={labelCls}>Role</label>
                      <div className="pt-1">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[roleColorKey] || roleColors.CUSTOM}`}>
                          {displayRole}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-border flex justify-end">
                  <Button onClick={handleUpdateProfile} disabled={saving} variant="primary" size="md">
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ── Vendor Edit Form ──────────────────────────────────────────── */}
          {selectedType === "vendor" && editVendor && (
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-content-primary">Edit Vendor</h2>
              </div>

              <div className="space-y-4">

                {/* Vendor Name */}
                <div>
                  <label className={labelCls}>Vendor Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="vendorName"
                    value={editVendor.vendorName}
                    onChange={handleVendorInputChange}
                    placeholder="Enter vendor name"
                    className={inputCls}
                  />
                </div>

                {/* Address 1 */}
                <div>
                  <label className={labelCls}>Address 1</label>
                  <input
                    type="text"
                    name="vendorAddress1"
                    value={editVendor.vendorAddress1}
                    onChange={handleVendorInputChange}
                    placeholder="Street address"
                    className={inputCls}
                  />
                </div>

                {/* Address 2 */}
                <div>
                  <label className={labelCls}>Address 2</label>
                  <input
                    type="text"
                    name="vendorAddress2"
                    value={editVendor.vendorAddress2}
                    onChange={handleVendorInputChange}
                    placeholder="Apt, suite, unit, etc."
                    className={inputCls}
                  />
                </div>

                {/* Contact No 1 | Contact No 2 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Phone No 1</label>
                    <input
                      type="text"
                      name="vendorContactNo1"
                      value={editVendor.vendorContactNo1}
                      onChange={handleVendorInputChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Phone No 2</label>
                    <input
                      type="text"
                      name="vendorContactNo2"
                      value={editVendor.vendorContactNo2}
                      onChange={handleVendorInputChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Vendor Owner | Domain */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Vendor Owner</label>
                    <input
                      type="text"
                      name="vendorOwner"
                      value={editVendor.vendorOwner}
                      onChange={handleVendorInputChange}
                      placeholder="Owner name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Domain</label>
                    <input
                      type="text"
                      name="vendorDomain"
                      value={editVendor.vendorDomain}
                      onChange={handleVendorInputChange}
                      placeholder="e.g., vendor.com"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={labelCls}>Description</label>
                  <textarea
                    name="vendorDescription"
                    value={editVendor.vendorDescription}
                    onChange={handleVendorInputChange}
                    placeholder="Brief description of the vendor"
                    rows={3}
                    className={`${inputCls} resize-none`}
                  />
                </div>

              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={handleUpdateVendor} disabled={saving} variant="primary" size="md">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Accounts;
